import React, {useEffect} from "react"
import {CmCollection} from "../../../../packages/graphql-layer/src";
import {Typography} from "@mui/material";
import Collapse from '@mui/material/Collapse';
import CCOHeadline from "../../fundamentals/CCOHeadline";
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineOppositeContent from '@mui/lab/TimelineOppositeContent';
import CCOPlainText from "../../fundamentals/CCOPlainText";
import CCOContainer from "../../fundamentals/CCOContainer";
import CCOComponentWrapper from "../../fundamentals/CCOComponentWrapper";
import CCOLinkText from "../../fundamentals/CCOLinkText";
import {useTranslation} from "react-i18next";
import CCOLink from "../../fundamentals/CCOLink";
import {Box} from "@mui/system";
import {metaDataElementWithId, metaDataProperty} from "../../preview/previewMetaData";
import {usePreviewContext} from "../../../context/PreviewContext";
import useWindowSize from "../../../utility/useWindowSize";


export interface Props {
    cmCollection: CmCollection
    CAEGlobalSettings: any
}

function C006DonationLine(props: Props) {
    const {t} = useTranslation();
    const isPreview = usePreviewContext().isPreview;
    const {lowerThanWindowMedium} = useWindowSize();
    const [open, setOpen] = React.useState(0);
    const [hover, setHover] = React.useState(0);
    const [scale, setScale] = React.useState(1);


    useEffect(() => {
        if (hover !== undefined) {
            // Scale dot on hover
            const interval = setInterval(() => {
                setScale((prevScale) => prevScale > 1.2 ? prevScale : prevScale + 0.01);
            }, 10);
            return () => {
                // Clear the interval when hover is false
                clearInterval(interval);
            };
        } else {
            // Reset scale when hover is false
            setScale(1);
        }
    }, [hover]);


    return <CCOComponentWrapper>
        <CCOContainer>
            <CCOHeadline id={props.cmCollection.id}
                         metadataId={props.cmCollection.id}
                         headline={props.cmCollection.teaserTitle}
                         type={"componenthead"}
                         propertyName='properties.teaserTitle'
                         subtextPropertyName='properties.teaserText'
                         subtext={props.cmCollection.teaserText?.textAsTree}/>
            <Timeline sx={{
                width: '100%', "& .MuiTimelineItem-root:before": {flex: 0,},
            }}>
                {(props.cmCollection?.items ?? []).map((item: any, index: number) => {
                        return <TimelineItem key={index} onClick={() => setOpen(index)}
                                             onMouseEnter={() => setHover(index)}
                                             sx={{width: '100%', paddingX: {xs: '0!important'}}}
                                             {...metaDataElementWithId(isPreview, item.id)}>
                            {item.headline && !lowerThanWindowMedium ?
                                <TimelineOppositeContent width={'30%'}
                                                         paddingTop={lowerThanWindowMedium ? '1!important' : '0!important'}>
                                    <Typography variant={'h4'}>{item.headline}</Typography>
                                </TimelineOppositeContent> : null}
                            <TimelineSeparator>
                                <TimelineDot sx={{
                                    color: 'primary.main',
                                    backgroundColor: open === index ? 'primary.contrastText' :
                                        (hover === index ? 'primary.light' : 'primary.main'),
                                    borderColor: 'primary.main',
                                    margin: 0,
                                    padding: 4,
                                    transform: hover === index ? `scale(${scale})` : undefined, // Apply the scale transformation
                                    transition: 'transform 0.2s',
                                }}/>
                                {index < props.cmCollection.items!.length - 1 ? <TimelineConnector
                                    sx={{
                                        backgroundColor: 'primary.main',
                                        height: '100%'
                                    }}/> : null}
                            </TimelineSeparator>
                            <TimelineContent marginX={{sx: 0, md: 5}} marginBottom={5} width={'40%'}
                                             paddingTop={lowerThanWindowMedium ? '1!important' : '0!important'}
                                             alignItems={'flex-start!important'}>
                                {lowerThanWindowMedium && <Typography variant={'h4'}>{item.headline}</Typography>}
                                <Typography variant={'h4'} {...metaDataProperty(isPreview, 'properties.teaserTitle', item.id)}>{item.teaserTitle}</Typography>
                                <Collapse in={open === index}>
                                    {renderTeaserText(item)}
                                    {renderTeaserTargetLink(item, t)}
                                </Collapse>
                            </TimelineContent>
                        </TimelineItem>
                    }
                )
                }
            </Timeline>
        </CCOContainer></CCOComponentWrapper>
}

function renderTeaserText(item: any) {
    if (item.teaserText && item.teaserText.textAsTree) {
        return <Box mt={5}>
            <CCOPlainText metadataId={item.id} propertyName='properties.teaserText' textAsTree={item.teaserText.textAsTree}/>
        </Box>
    } else return null;
}

function renderTeaserTargetLink(item: any, t: any) {
    const ctaSettings = item.carouselSettings;
    if (item.teaserTarget && ctaSettings && ctaSettings.callToActionEnabled) {
        return <Box mt={5}>
            <CCOLink target={item.teaserTarget}>
                <CCOLinkText txt={ctaSettings.callToActionCustomText || t('c014_default_call_to_action_text')}
                             hasFocusedStyle/>
            </CCOLink>
        </Box>
    } else return null;
}

export default C006DonationLine